import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MessageSquare, X, Send, Smile, Share2, Minimize2, Maximize2, Play, Search } from 'lucide-react';
import { ChatMessage, QuickEmojiPicker } from './ui';
import { d1List, d1Upsert, d1Delete, d1BatchUpsert } from '@/lib/d1-storage';
import { generateId, now } from '@/lib/utils';
import { CATEGORIES } from '@/data';
import type { CoupleMessage, Clip } from '@/types';

interface FloatingChatProps {
  coupleId: string;
  userType: 'couple' | 'reviewer';
  userName: string;
  partnerLabel?: string; // "Your Reviewer" or "John & Jane"
  clips?: Clip[];
  accentColor?: 'pink' | 'lime';
  embedded?: boolean; // If true, renders inline instead of as a floating button
  aboveTabBar?: boolean; // Lift the floating button clear of a fixed bottom tab bar
}

export const FloatingChat: React.FC<FloatingChatProps> = ({
  coupleId,
  userType,
  userName,
  partnerLabel = 'Chat',
  clips = [],
  accentColor = 'pink',
  embedded = false,
  aboveTabBar = false,
}) => {
  const floatingPositionClass = aboveTabBar ? 'bottom-[88px] right-4 lg:bottom-6 lg:right-6' : 'bottom-4 right-4';
  const [isExpanded, setIsExpanded] = useState(embedded); // Start expanded if embedded
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [messages, setMessages] = useState<CoupleMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showClipPicker, setShowClipPicker] = useState(false);
  const [selectedClip, setSelectedClip] = useState<Clip | null>(null);
  const [clipSearch, setClipSearch] = useState('');
  const [clipCategory, setClipCategory] = useState<string>('all');
  const [sendError, setSendError] = useState<string | null>(null);
  const [pendingMessage, setPendingMessage] = useState<{ message: string; clipId?: string } | null>(null);

  // Get categories that have clips
  const categoriesWithClips = useMemo(() => {
    const catIds = new Set(clips.map(c => c.categoryId));
    return CATEGORIES.filter(cat => catIds.has(cat.id));
  }, [clips]);

  // Filter clips based on search and category
  const filteredClips = useMemo(() => {
    let result = clips;
    if (clipCategory !== 'all') {
      result = result.filter(c => c.categoryId === clipCategory);
    }
    if (clipSearch.trim()) {
      const search = clipSearch.toLowerCase();
      result = result.filter(c =>
        c.title.toLowerCase().includes(search) ||
        c.description?.toLowerCase().includes(search)
      );
    }
    return result;
  }, [clips, clipCategory, clipSearch]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const prevMessageCountRef = useRef(0);
  const justSentRef = useRef(false);

  // Accent colors based on user type
  const colors = accentColor === 'pink'
    ? { bg: 'from-pink-500 to-purple-600', accent: 'pink-500', text: 'pink-400', badge: 'pink-500' }
    : { bg: 'from-lime-500 to-green-600', accent: 'lime-500', text: 'lime-400', badge: 'lime-500' };

  // Track if we're currently sending to prevent race conditions
  const sendingRef = useRef(false);

  // Fetch messages
  const fetchMessages = async () => {
    if (!coupleId) return;
    // Don't fetch while sending - prevents race condition where polling
    // overwrites the optimistic update before d1Upsert completes
    if (sendingRef.current) return;

    const parsed = await d1List<CoupleMessage>('couple_messages', { coupleId });
    setMessages(parsed);
  };

  // Load messages on mount and when expanded
  useEffect(() => {
    if (coupleId) {
      fetchMessages();
    }
  }, [coupleId]);

  // Refresh messages when expanded
  useEffect(() => {
    if (isExpanded && coupleId) {
      fetchMessages();
      // Mark as seen
      markAsSeen();
    }
  }, [isExpanded]);

  // Poll for new messages every 10 seconds when expanded
  useEffect(() => {
    if (!isExpanded || !coupleId) return;
    const interval = setInterval(fetchMessages, 10000);
    return () => clearInterval(interval);
  }, [isExpanded, coupleId]);

  // Scroll to bottom on open or fullscreen toggle
  useEffect(() => {
    if (isExpanded) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'instant', block: 'end' });
      }, 50);
    }
  }, [isExpanded, isFullscreen]);

  // Scroll to bottom only when new messages arrive and user is near bottom (or just sent)
  useEffect(() => {
    const container = messagesContainerRef.current;
    const hasNewMessages = messages.length > prevMessageCountRef.current;

    // Check if user is near bottom (within 100px)
    const isNearBottom = container
      ? container.scrollHeight - container.scrollTop - container.clientHeight < 100
      : true;

    // Scroll if: user just sent a message, OR new messages arrived and user is near bottom
    if (justSentRef.current || (hasNewMessages && isNearBottom)) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      justSentRef.current = false;
    }

    prevMessageCountRef.current = messages.length;
  }, [messages]);

  // Mark messages as seen
  const markAsSeen = async () => {
    if (!coupleId || messages.length === 0) return;
    const seenField = userType === 'couple' ? 'seenByCouple' : 'seenByReviewer';
    const updated = messages.map(m => ({ ...m, [seenField]: true }));
    const toUpdate = messages.filter(m => !(m as unknown as Record<string, unknown>)[seenField]);
    if (toUpdate.length > 0) {
      await d1BatchUpsert('couple_messages', toUpdate.map(m => ({ ...m, [seenField]: true })));
    }
    setMessages(updated);
  };

  // Send message with retry support
  const sendMessage = async (clipId?: string, isRetry = false) => {
    if (!coupleId) return;

    // Use pending message for retry, otherwise use current input
    const messageText = isRetry && pendingMessage ? pendingMessage.message : inputMessage.trim();
    const messageClipId = isRetry && pendingMessage ? pendingMessage.clipId : clipId;

    if (!messageText && !messageClipId) return;

    // Store pending message before attempting send
    if (!isRetry) {
      setPendingMessage({ message: messageText, clipId: messageClipId });
    }

    setSending(true);
    sendingRef.current = true; // Prevent polling from overwriting during send
    setSendError(null);

    try {
      const newMessage: CoupleMessage = {
        id: generateId('msg'),
        coupleId,
        fromType: userType,
        fromName: userName,
        message: messageText,
        createdAt: now(),
        seenByCouple: userType === 'couple',
        seenByReviewer: userType === 'reviewer',
        ...(messageClipId && { clipId: messageClipId }),
      };

      await d1Upsert('couple_messages', newMessage);

      // Success - clear everything
      justSentRef.current = true; // Ensure we scroll to the new message
      setMessages(prev => [...prev, newMessage]);
      setInputMessage('');
      setSelectedClip(null);
      setShowClipPicker(false);
      setShowEmojiPicker(false);
      setPendingMessage(null);
      setSendError(null);

      // Focus input after sending
      inputRef.current?.focus();
    } catch (error) {
      // Failed - keep pending message for retry
      setSendError('Failed to send. Tap to retry.');
      console.error('Chat send error:', error);
    } finally {
      setSending(false);
      sendingRef.current = false; // Allow polling again
    }
  };

  // Retry sending failed message
  const retrySend = () => {
    if (pendingMessage) {
      sendMessage(pendingMessage.clipId, true);
    }
  };

  // Cancel failed message
  const cancelPending = () => {
    setPendingMessage(null);
    setSendError(null);
  };

  // Count unread messages (include system messages for couples)
  const unreadCount = messages.filter(m => {
    if (userType === 'couple') return (m.fromType === 'reviewer' || m.fromType === 'system') && !m.seenByCouple;
    return m.fromType === 'couple' && !m.seenByReviewer;
  }).length;

  // Handle emoji selection
  const handleEmoji = (emoji: string) => {
    setInputMessage(prev => prev + emoji);
    inputRef.current?.focus();
  };

  // Handle clip selection
  const handleClipSelect = (clip: Clip) => {
    setSelectedClip(clip);
    setShowClipPicker(false);
  };

  // Send with clip
  const handleSendWithClip = () => {
    sendMessage(selectedClip?.id);
  };

  if (!coupleId) return null;

  // Embedded mode: render inline without floating/fixed positioning
  if (embedded) {
    return (
      <div className="w-full h-[400px] bg-zinc-800/50 rounded-xl border border-zinc-700 flex flex-col overflow-hidden">
        {/* Messages */}
        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-3 space-y-3">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-zinc-500">
              <MessageSquare className="w-10 h-10 mb-2 opacity-50" />
              <p className="text-sm">No messages yet</p>
              <p className="text-xs mt-1">Start a conversation!</p>
            </div>
          ) : (
            <>
              {messages.map((msg) => (
                <ChatMessage
                  key={msg.id}
                  message={msg}
                  isOwn={msg.fromType === userType}
                  clips={clips}
                />
              ))}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Error Banner */}
        {sendError && (
          <div className="bg-red-500/20 border-t border-red-500/30 px-3 py-2 flex items-center justify-between">
            <span className="text-red-400 text-xs">{sendError}</span>
            <div className="flex gap-2">
              <button onClick={retrySend} className="text-red-400 hover:text-red-300 text-xs font-medium">Retry</button>
              <button onClick={cancelPending} className="text-zinc-400 hover:text-zinc-300 text-xs">Cancel</button>
            </div>
          </div>
        )}

        {/* Input */}
        <div className="border-t border-zinc-700 p-2">
          <div className="flex gap-2 items-end">
            <div className="flex-1 relative">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onFocus={markAsSeen}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(selectedClip?.id);
                  }
                }}
                placeholder="Type a message..."
                className="w-full bg-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-lime-500"
                disabled={sending}
              />
            </div>
            <button
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className="text-zinc-400 hover:text-zinc-200 p-2"
            >
              <Smile className="w-5 h-5" />
            </button>
            {clips.length > 0 && (
              <button
                onClick={() => setShowClipPicker(!showClipPicker)}
                className={`text-zinc-400 hover:text-zinc-200 p-2 ${selectedClip ? 'text-lime-400' : ''}`}
              >
                <Share2 className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={() => sendMessage(selectedClip?.id)}
              disabled={sending || (!inputMessage.trim() && !selectedClip)}
              className={`p-2 rounded-lg transition-colors ${
                sending || (!inputMessage.trim() && !selectedClip)
                  ? 'bg-zinc-700 text-zinc-500'
                  : `bg-lime-500 text-white hover:bg-lime-600`
              }`}
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          {showEmojiPicker && (
            <div className="mt-2">
              <QuickEmojiPicker onSelect={handleEmoji} />
            </div>
          )}
          {selectedClip && (
            <div className="mt-2 flex items-center gap-2 bg-zinc-700/50 rounded-lg p-2">
              <Play className="w-4 h-4 text-lime-400" />
              <span className="text-xs text-zinc-300 flex-1 truncate">{selectedClip.title}</span>
              <button onClick={() => setSelectedClip(null)} className="text-zinc-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed z-50 ${isFullscreen ? 'inset-0' : floatingPositionClass}`}>
      {/* Collapsed Bubble */}
      {!isExpanded && (
        <button
          onClick={() => setIsExpanded(true)}
          className={`w-14 h-14 rounded-full bg-gradient-to-br ${colors.bg} shadow-lg hover:scale-110 transition-transform flex items-center justify-center relative`}
        >
          <MessageSquare className="w-6 h-6 text-white" />
          {unreadCount > 0 && (
            <span className={`absolute -top-1 -right-1 w-5 h-5 bg-${colors.badge} rounded-full text-white text-xs font-bold flex items-center justify-center`}>
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Expanded Panel */}
      {isExpanded && (
        <div className={`${isFullscreen
          ? 'fixed inset-4 w-auto h-auto max-h-none'
          : 'w-80 h-[60vh] max-h-[500px]'
        } bg-zinc-900 rounded-2xl shadow-2xl border border-zinc-700 flex flex-col overflow-hidden`}>
          {/* Header */}
          <div className={`bg-gradient-to-r ${colors.bg} px-4 py-3 flex items-center justify-between`}>
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-white" />
              <span className="text-white font-medium text-sm">{partnerLabel}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="text-white/60 hover:text-white/90"
                title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
              >
                {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
              <button
                onClick={() => {
                  markAsSeen();
                  setIsExpanded(false);
                  setIsFullscreen(false);
                }}
                className="text-white/80 hover:text-white"
                title="Minimize"
              >
                <Minimize2 className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-zinc-500">
                <MessageSquare className="w-10 h-10 mb-2 opacity-50" />
                <p className="text-sm">No messages yet</p>
                <p className="text-xs">Start the conversation!</p>
              </div>
            ) : (
              messages.map(msg => (
                <ChatMessage
                  key={msg.id}
                  message={msg}
                  isOwn={msg.fromType === userType}
                  clips={clips}
                  onDelete={async (messageId) => {
                    await d1Delete('couple_messages', messageId);
                    setMessages(messages.filter(m => m.id !== messageId));
                  }}
                />
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Selected Clip Preview */}
          {selectedClip && (
            <div className="px-3 pb-2">
              <div className="bg-zinc-800 rounded-lg p-2 flex items-center gap-2">
                <div className="w-12 h-12 bg-zinc-700 rounded flex items-center justify-center">
                  <Play className="w-4 h-4 text-zinc-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs truncate">{selectedClip.title}</p>
                </div>
                <button onClick={() => setSelectedClip(null)} className="text-zinc-400 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Emoji Picker */}
          {showEmojiPicker && (
            <div className="px-3 pb-2">
              <QuickEmojiPicker onSelect={handleEmoji} />
            </div>
          )}

          {/* Enhanced Clip Picker */}
          {showClipPicker && clips.length > 0 && (
            <div className="border-t border-zinc-700">
              {/* Search */}
              <div className="px-3 py-2 border-b border-zinc-700/50">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <input
                    type="text"
                    value={clipSearch}
                    onChange={e => setClipSearch(e.target.value)}
                    placeholder="Search clips..."
                    className="w-full bg-zinc-800 border border-zinc-600 rounded-lg pl-8 pr-3 py-1.5 text-white text-xs focus:outline-none focus:border-pink-500"
                  />
                </div>
              </div>

              {/* Category Filter */}
              <div className="px-2 py-1.5 flex gap-1 overflow-x-auto border-b border-zinc-700/50">
                <button
                  onClick={() => setClipCategory('all')}
                  className={`px-2 py-1 rounded text-xs whitespace-nowrap ${clipCategory === 'all' ? 'bg-pink-500 text-white' : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'}`}
                >
                  All ({clips.length})
                </button>
                {categoriesWithClips.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setClipCategory(cat.id)}
                    className={`px-2 py-1 rounded text-xs whitespace-nowrap flex items-center gap-1 ${clipCategory === cat.id ? 'bg-pink-500 text-white' : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'}`}
                  >
                    <span>{cat.emoji}</span>
                    <span className="hidden sm:inline">{clips.filter(c => c.categoryId === cat.id).length}</span>
                  </button>
                ))}
              </div>

              {/* Clip Grid */}
              <div className="px-2 py-2 max-h-48 overflow-y-auto">
                {filteredClips.length === 0 ? (
                  <p className="text-zinc-500 text-xs text-center py-4">No clips found</p>
                ) : (
                  <div className="grid grid-cols-1 gap-1">
                    {filteredClips.slice(0, 20).map(clip => {
                      const cat = CATEGORIES.find(c => c.id === clip.categoryId);
                      return (
                        <button
                          key={clip.id}
                          onClick={() => handleClipSelect(clip)}
                          className="bg-zinc-800 hover:bg-zinc-700 rounded-lg p-2 text-left flex items-center gap-2"
                        >
                          <span className="text-base flex-shrink-0">{cat?.emoji}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-xs truncate">{clip.title}</p>
                            <p className="text-zinc-500 text-[10px] truncate">{cat?.name}</p>
                          </div>
                        </button>
                      );
                    })}
                    {filteredClips.length > 20 && (
                      <p className="text-zinc-500 text-xs text-center py-1">+{filteredClips.length - 20} more</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Send Error / Retry Banner */}
          {sendError && pendingMessage && (
            <div className="px-3 py-2 bg-red-500/20 border-t border-red-500/30">
              <div className="flex items-center justify-between gap-2">
                <p className="text-red-400 text-xs flex-1">{sendError}</p>
                <div className="flex gap-2">
                  <button
                    onClick={cancelPending}
                    className="px-2 py-1 text-xs text-zinc-400 hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={retrySend}
                    disabled={sending}
                    className="px-2 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-400 disabled:opacity-50"
                  >
                    {sending ? 'Sending...' : 'Retry'}
                  </button>
                </div>
              </div>
              {pendingMessage.message && (
                <p className="text-zinc-400 text-xs mt-1 truncate">"{pendingMessage.message}"</p>
              )}
            </div>
          )}

          {/* Input Area */}
          <div className="p-3 border-t border-zinc-700">
            <div className="flex items-end gap-2">
              <div className="flex gap-1">
                <button
                  onClick={() => {
                    setShowEmojiPicker(!showEmojiPicker);
                    setShowClipPicker(false);
                  }}
                  className={`p-2 rounded-lg ${showEmojiPicker ? `bg-${colors.accent}/20 text-${colors.text}` : 'text-zinc-400 hover:text-white'}`}
                >
                  <Smile className="w-5 h-5" />
                </button>
                {clips.length > 0 && (
                  <button
                    onClick={() => {
                      setShowClipPicker(!showClipPicker);
                      setShowEmojiPicker(false);
                    }}
                    className={`p-2 rounded-lg ${showClipPicker ? `bg-${colors.accent}/20 text-${colors.text}` : 'text-zinc-400 hover:text-white'}`}
                  >
                    <Share2 className="w-5 h-5" />
                  </button>
                )}
              </div>
              <textarea
                ref={inputRef}
                value={inputMessage}
                onChange={e => setInputMessage(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    selectedClip ? handleSendWithClip() : sendMessage();
                  }
                }}
                placeholder="Type a message..."
                className={`flex-1 bg-zinc-800 border border-zinc-600 rounded-xl px-3 py-2 text-white text-sm resize-none focus:outline-none focus:border-${colors.accent}`}
                rows={1}
              />
              <button
                onClick={() => selectedClip ? handleSendWithClip() : sendMessage()}
                disabled={sending || (!inputMessage.trim() && !selectedClip)}
                className={`p-2 rounded-xl bg-gradient-to-r ${colors.bg} text-white disabled:opacity-50`}
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloatingChat;
